import React, { useEffect, useMemo, useState } from 'react'
import { Container, Row, Col, Button, Form, Badge } from 'react-bootstrap'
import axios from 'axios'
import Swal from 'sweetalert2'

import Card from '../../../components/Card'
import ProfileHeader from '../../../components/profile-header'
import img6 from '../../../assets/images/page-img/profile-bg6.jpg'
import NoDataFound from '../../../components/noDataFound'
import { apiHandler } from '../../../api/apihandler'
import { QUICKBOOK_CARD_SAVE, QUICKBOOK_CARD_LIST, QUICKBOOK_CARD_DELETE } from '../../../api/constants'

const getNormalizedCards = (res) => {
  const source = res?.data?.data?.user_quickbook_cards || res?.data?.data?.cards || res?.data?.data?.data || []
  const list = Array.isArray(source) ? source : (Array.isArray(source?.data) ? source.data : [])

  return list
    .filter((card) => Number(card?.status ?? 1) === 1)
    .map((card) => ({
      id: card?.id || card?.user_quickbook_card_id || card?.userQuickbookCardId,
      quickbookCardId: card?.quickbook_card_id || card?.quickbookCardId,
      name: card?.name,
      expYear: card?.exp_year || card?.expYear,
      expMonth: card?.exp_month || card?.expMonth,
      brand: card?.brand || card?.card_brand,
      last4: card?.last4 || card?.card_last4,
      status: card?.status,
      createdAt: card?.created_at || card?.createdAt,
    }))
    .filter((card) => card?.id)
}

const formatCreatedDate = (createdAt) => {
  if (!createdAt) return '-'
  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString()
}

const formatCardLabel = (card) => {
  const namePart = card?.name || card?.brand || 'QuickBooks Card'
  const last4Part = card?.last4 ? ` ending ${card.last4}` : ''
  const expPart = card?.expMonth || card?.expYear ? ` (${card?.expMonth || '--'}/${card?.expYear || '--'})` : ''
  return `${namePart}${last4Part}${expPart}`
}

function AddCardForm({ onAdded }) {
  const [holderName, setHolderName] = useState('')
  const [cardNumber, setCardNumber] = useState('')
  const [expMonth, setExpMonth] = useState('01')
  const [expYear, setExpYear] = useState(String(new Date().getFullYear()))
  const [cvv, setCvv] = useState('')
  const [zip, setZip] = useState('')
  const [city, setCity] = useState('')
  const [region, setRegion] = useState('')
  const [streetAddress, setStreetAddress] = useState('')
  const [country, setCountry] = useState('US')
  const [submitting, setSubmitting] = useState(false)

  const currentYear = new Date().getFullYear()
  const yearsArray = useMemo(() => Array.from({ length: 10 }, (_, index) => String(currentYear + index)), [currentYear])

  const formatCardInput = (value) => {
    const raw = value.replace(/\D/g, '').slice(0, 16)
    return raw.replace(/(.{4})/g, '$1 ').trim()
  }

  const resetForm = () => {
    setHolderName('')
    setCardNumber('')
    setExpMonth('01')
    setExpYear(String(currentYear))
    setCvv('')
    setZip('')
    setCity('')
    setRegion('')
    setStreetAddress('')
    setCountry('US')
  }

  const addNewCard = async () => {
    const cleanNumber = cardNumber.replace(/\s/g, '')

    if (!cleanNumber || cleanNumber.length < 12) {
      Swal.fire({ title: 'Required', text: 'Please enter a valid card number.', icon: 'warning', confirmButtonText: 'Okay' })
      return
    }

    if (!cvv.trim() || cvv.trim().length < 3) {
      Swal.fire({ title: 'Required', text: 'Please enter a valid CVV.', icon: 'warning', confirmButtonText: 'Okay' })
      return
    }

    setSubmitting(true)
    try {
      const tokenApiUrl = process.env.REACT_APP_QUICKBOOK_TOKEN_URL || 'https://api.intuit.com/quickbooks/v4/payments/tokens'
      const cardPayload = {
        name: holderName.trim(),
        number: cleanNumber,
        expMonth,
        expYear,
        cvc: cvv.trim(),
        address: {
          postalCode: zip,
          country: country || 'US',
          region,
          streetAddress,
          city
        }
      }

      const tokenResponse = await axios.post(
        tokenApiUrl,
        { card: cardPayload },
        { headers: { 'Content-Type': 'application/json' } }
      )

      const quickbookToken = tokenResponse?.data?.value
      if (!quickbookToken) {
        Swal.fire({ title: 'Error', text: 'Unable to tokenize this card with QuickBooks.', icon: 'error', confirmButtonText: 'Okay' })
        return
      }

      const saveRes = await apiHandler(QUICKBOOK_CARD_SAVE, 'POST', {
        token: quickbookToken,
        name: holderName.trim() || undefined,
      })

      if (saveRes?.data?.response?.status !== 200) {
        Swal.fire({
          title: 'Error',
          text: saveRes?.data?.response?.message || 'Unable to save card right now.',
          icon: 'error',
          confirmButtonText: 'Okay'
        })
        return
      }

      resetForm()
      await onAdded()
      Swal.fire({ title: 'Saved', text: saveRes?.data?.response?.message || 'Card saved successfully.', icon: 'success', confirmButtonText: 'Okay' })
    } catch (error) {
      const message = error?.response?.data?.errors?.[0]?.message || error?.response?.data?.response?.message || 'Something went wrong while saving card.'
      Swal.fire({ title: 'Error', text: message, icon: 'error', confirmButtonText: 'Okay' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Form.Group className='mb-3'>
        <Form.Label className='fw-semibold'>Card Holder Name (Optional)</Form.Label>
        <Form.Control
          type='text'
          value={holderName}
          onChange={(e) => setHolderName(e.target.value)}
          placeholder='Enter card holder name'
        />
      </Form.Group>

      <Form.Group className='mb-3'>
        <Form.Label className='fw-semibold'>Card Details</Form.Label>
        <Form.Control
          type='text'
          value={cardNumber}
          onChange={(e) => setCardNumber(formatCardInput(e.target.value))}
          placeholder='1234 5678 9012 3456'
        />
      </Form.Group>

      <Row>
        <Col xs={4}>
          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold'>Month</Form.Label>
            <Form.Select value={expMonth} onChange={(e) => setExpMonth(e.target.value)}>
              {Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0')).map((month) => (
                <option key={month} value={month}>{month}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={4}>
          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold'>Year</Form.Label>
            <Form.Select value={expYear} onChange={(e) => setExpYear(e.target.value)}>
              {yearsArray.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={4}>
          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold'>CVV</Form.Label>
            <Form.Control
              type='password'
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder='123'
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className='mb-3'>
        <Form.Label className='fw-semibold'>Street Address</Form.Label>
        <Form.Control
          type='text'
          value={streetAddress}
          onChange={(e) => setStreetAddress(e.target.value)}
          placeholder='Street address'
        />
      </Form.Group>

      <Row>
        <Col md={4}>
          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold'>City</Form.Label>
            <Form.Control
              type='text'
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder='City'
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold'>State/Region</Form.Label>
            <Form.Control
              type='text'
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder='State'
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold'>ZIP</Form.Label>
            <Form.Control
              type='text'
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder='ZIP'
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className='mb-3'>
        <Form.Label className='fw-semibold'>Country</Form.Label>
        <Form.Control
          type='text'
          value={country}
          onChange={(e) => setCountry(e.target.value.toUpperCase())}
          placeholder='US'
          maxLength={2}
        />
      </Form.Group>

      <Button className='w-100' onClick={addNewCard} disabled={submitting}>
        {submitting ? 'Saving...' : 'Add New Card'}
      </Button>
    </>
  )
}

const SavedCardList = () => {
  const [cards, setCards] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const fetchSavedCards = async () => {
    setIsLoading(true)
    try {
      const res = await apiHandler(QUICKBOOK_CARD_LIST, 'POST', {})
      if (res?.data?.response?.status === 200) {
        setCards(getNormalizedCards(res))
      } else {
        setCards([])
      }
    } catch (error) {
      setCards([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSavedCards()
  }, [])

  const removeCard = async (id) => {
    const res = await Swal.fire({
      title: 'Delete Card?',
      text: 'This card will be removed from your saved card list.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    })

    if (!res.isConfirmed) return

    try {
      const response = await apiHandler(QUICKBOOK_CARD_DELETE, 'POST', {
        user_quickbook_card_id: id,
      })

      if (response?.data?.response?.status === 200) {
        await fetchSavedCards()
        Swal.fire({ title: 'Deleted', text: response?.data?.response?.message || 'Card deleted successfully.', icon: 'success', confirmButtonText: 'Okay' })
      } else {
        Swal.fire({ title: 'Error', text: response?.data?.response?.message || 'Unable to delete card.', icon: 'error', confirmButtonText: 'Okay' })
      }
    } catch (error) {
      Swal.fire({ title: 'Error', text: error?.response?.data?.response?.message || 'Unable to delete card.', icon: 'error', confirmButtonText: 'Okay' })
    }
  }

  return (
    <>
      <ProfileHeader title='Saved Cards' img={img6} />
      <div id='content-page' className='content-page'>
        <Container>
          <Row className='g-4'>
            <Col lg={8}>
              <Card>
                <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                  <div className='header-title'>
                    <h4 className='card-title mb-1'>Saved Card List</h4>
                    <p className='text-muted mb-0'>Manage cards saved to your account.</p>
                  </div>
                  <Badge bg='primary' pill>{cards.length} Cards</Badge>
                </Card.Header>
                <Card.Body>
                  {isLoading ? (
                    <p className='text-muted mb-0'>Loading cards...</p>
                  ) : cards.length === 0 ? (
                    <NoDataFound txtData='No saved cards found.' />
                  ) : (
                    <div className='list-group list-group-flush'>
                      {cards.map((card, index) => (
                        <div
                          key={card.id}
                          className='list-group-item px-0 py-3'
                          style={{ borderTop: index === 0 ? 'none' : undefined }}
                        >
                          <div className='d-flex align-items-center justify-content-between gap-2 flex-wrap'>
                            <div>
                              <div className='fw-semibold text-dark'>{formatCardLabel(card)}</div>
                              <div className='text-muted' style={{ fontSize: '12px' }}>
                                ID: {card.id} · Added {formatCreatedDate(card.createdAt)}
                              </div>
                            </div>
                            <Button variant='outline-danger' size='sm' onClick={() => removeCard(card.id)}>
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card>
                <Card.Header>
                  <div className='header-title'>
                    <h5 className='card-title mb-0'>Add New Card</h5>
                  </div>
                </Card.Header>
                <Card.Body>
                  <AddCardForm onAdded={fetchSavedCards} />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  )
}

export default SavedCardList
